import { AxiosBaseQueryFunction } from 'api/axiosBaseQuery';

import { DEFAULT_EDGE_NAME, EDGE_BRAND_RULES } from '@common/constants';
import { ColorModel, EdgeModel, MaterialModel } from '@common/models';
import { createFetchProductsHook } from '@core/utils/createFetchProductsHook';
import { useSelectedColor } from '@ee-api/colors-api/useSelectedColor';
import { useSelectedMaterial } from '@ee-api/materials-api/useSelectedMaterial';
import { CommonProductQueryParams } from '@ee-api/queryParams';
import { createSelector } from '@reduxjs/toolkit';
import { TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';

import { useFetchEdgesQuery } from './edgesService';

/**
 * Returns -1, 0 or 1 for sorting order, but prioritizes non-null values ahead of null values
 * if both null, or equal, return 0
 * if one is null and the other not, return -1 or 1 depending on whether a or b is non-null, respectively
 * if both have values, do a simple <, =, > check to determine ordering
 * @param a first value to check
 * @param b second value to check
 */
export const orderValuesBeforeNulls = (a: string | number | null, b: string | number | null): number => {
    const isANull = a === null || Number.isNaN(a); // not just falsy, but actually null
    const isBNull = b === null || Number.isNaN(b);
    if (isANull && isBNull) {
        return 0;
    }
    if (isBNull) {
        // a is not null, it comes first
        return -1;
    }
    if (isANull) {
        // b is not null, it comes first
        return 1;
    }

    // eslint-disable-next-line no-nested-ternary
    return a < b ? -1 : a === b ? 0 : 1; //negative if a < b, zero if equal, positive if a > b
};

/**
 * sort the edge list by up to four attributes
 *  - rank (if given, may be null)
 *  - by the original "DEFAULT_EDGE_NAME" (if there's a match)
 *  - by the price
 *  - by the name of the edge (for duplicate prices)
 *  Whichever is first in the above order is the default selection
 * @param edgeData
 */
export const sortEdges = (a: EdgeModel, b: EdgeModel) => {
    // rankOrder will be -1 if a.rank < b.rank OR if b.rank is null, opposite for b>a or a null, zero if both equal or both null
    const rankOrder = orderValuesBeforeNulls(a.rank, b.rank);

    // does a or be include the DEFAULT_EDGE_NAME?
    const isADefault = a.name?.includes(DEFAULT_EDGE_NAME);
    const isBDefault = b.name?.includes(DEFAULT_EDGE_NAME);
    const defaultNameOrder = Number(isBDefault) - Number(isADefault); //b-a to invert the result

    // simple numeric comparison on price
    const priceOrder = Math.sign(a.price - b.price);

    const nameOrder = orderValuesBeforeNulls(a.name, b.name);

    // return the first non-zero value in our preferred order
    return rankOrder || defaultNameOrder || priceOrder || nameOrder;
};

/**
 * Defining empty array outside of selector as indicated in docs.
 * @see {@link https://redux.js.org/tutorials/essentials/part-8-rtk-query-advanced#selecting-values-from-results}
 */
const emptyArray = [] satisfies EdgeModel[];

export const selectSortedEdges = createSelector([(data?: EdgeModel[]) => data], (data) =>
    (data ?? emptyArray).toSorted(sortEdges)
);

const _useEdges = createFetchProductsHook({
    apiQueryHook: useFetchEdgesQuery,
});

export const selectFilteredEdges = createSelector(
    [
        (edges: EdgeModel[] | undefined) => edges,
        (_edges, selectedMaterial: MaterialModel | undefined) => selectedMaterial,
        (_edges, _selectedMaterial, selectedColor: ColorModel | undefined) => selectedColor,
    ],
    (edges, selectedMaterial, selectedColor) => {
        if (!edges?.length || !EDGE_BRAND_RULES?.length || !selectedColor?.brand || !selectedMaterial?.description) {
            return (edges ?? emptyArray).toSorted(sortEdges);
        }

        const rule = EDGE_BRAND_RULES.find(
            (rule) => selectedMaterial.description?.match(rule.material) && selectedColor.brand?.match(rule.colorBrand)
        );

        return (!rule ? edges : edges.filter((edge) => !edge.brand?.match(rule.excludeBrand))).toSorted(sortEdges);
    }
);

export const useEdges = (): Omit<
    TypedUseQueryHookResult<EdgeModel[], CommonProductQueryParams, AxiosBaseQueryFunction>,
    'data'
> & {
    sortedEdges: EdgeModel[];
} => {
    const { selectedValue: selectedMaterial } = useSelectedMaterial();
    const { selectedValue: selectedColor } = useSelectedColor();

    return _useEdges({
        selectFromResult: (result) => ({
            sortedEdges: selectFilteredEdges(
                result.isError || !result.data ? emptyArray : result.data,
                selectedMaterial,
                selectedColor
            ),
            ...result,
        }),
    });
};
